import type { MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ShopType } from '@schema/cart';
import type { ShopFragmentMainEndpointFragment } from '@schema/main';
import { useShops } from '@customHooks/useShops/useShops';
import { increaseCounter } from '@utils/yandexMetrica';

const YANDEX_RESERVE_BASE_URL =
  'https://reservation.yandex.ru/partner-reserve/forms';

export const getYandexReserveUrl = (
  shop?: Pick<ShopFragmentMainEndpointFragment, 'yandex_reserve'> | null
): string | null =>
  shop?.yandex_reserve
    ? `${YANDEX_RESERVE_BASE_URL}/${shop.yandex_reserve}`
    : null;

export const trackBookTableClick = (): void => {
  increaseCounter(5736784, 'click_book_table');
};

interface UseBookTable {
  oysterBars: ReturnType<typeof useShops>['oysterBars'];
  selectedBarId: string;
  setSelectedBarId: (id: string) => void;
  selectedAddress?: string | null;
  reserveUrl: string | null;
  routeUrl: string | null;
  handleBookingClick: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export const useBookTable = (
  shopInfo?: ShopFragmentMainEndpointFragment
): UseBookTable => {
  const { shops, oysterBars } = useShops({ isOnlyBars: true });

  const [selectedBarId, setSelectedBarId] = useState('');

  useEffect(() => {
    if (shopInfo?.id && shopInfo.type === ShopType.OysterBar) {
      setSelectedBarId(shopInfo.id.toString());
    }
  }, [shopInfo]);

  const selectedRestaurant = useMemo(() => {
    if (!selectedBarId) return null;
    return oysterBars.find(
      restaurant => restaurant.value === parseInt(selectedBarId)
    );
  }, [oysterBars, selectedBarId]);

  const selectedShop = useMemo(() => {
    if (!selectedBarId) return null;
    return shops.find(shop => shop.id === parseInt(selectedBarId)) ?? null;
  }, [shops, selectedBarId]);

  const reserveUrl = useMemo(
    () => getYandexReserveUrl(selectedShop),
    [selectedShop]
  );

  const routeUrl = useMemo(() => {
    const [lat, lon] = selectedShop?.geo ?? [];
    if (lat != null && lon != null) {
      return `https://yandex.ru/maps/?mode=routes&rtext=~${lat},${lon}&rtt=auto`;
    }
    if (selectedShop?.address) {
      return `https://yandex.ru/maps/?mode=routes&rtext=~${encodeURIComponent(
        selectedShop.address
      )}&rtt=auto`;
    }
    return null;
  }, [selectedShop]);

  const handleBookingClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!reserveUrl) {
      event.preventDefault();
      return;
    }

    trackBookTableClick();
  };

  return {
    oysterBars,
    selectedBarId,
    setSelectedBarId,
    selectedAddress: selectedRestaurant?.address,
    reserveUrl,
    routeUrl,
    handleBookingClick
  };
};
